/**
 * GameSetupPage — the one setup page behind both New Game (create) and Edit
 * Game (a lobby's host). Layout G1 (docs/setup-redesign/README.md):
 *
 *   Map card → special rules (boards with their own) → 1 Game setup → 2 Rules
 *
 * Desktop (>= 1024) pins a "Your game" summary (create) or the pending
 * changes (edit) with the primary action in a right-hand rail; tablet and
 * phone pin them in a footer. The screens own loading, saving and
 * navigation; this page owns layout and the working copy's limits.
 */
import React from 'react'
import { View, Text, TouchableOpacity, ScrollView, Keyboard } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { mapSeatRange, type MapDef } from '@warcarp/api-types'
import { teamsConfigOf } from '@warcarp/api-types'
import { COMPACT_HEADER_STYLE } from '../../../lib/gameChrome'
import { usePageLayout } from '../../../hooks/usePageLayout'
import { KeyboardAwareScrollView } from '../../ui/KeyboardAwareScrollView'
import { Button } from '../../ui/Button'
import { MapPickerModal } from '../MapPickerModal'
import type { MapPickerOption } from '../../../lib/mapPickerOptions'
import {
  changesFooterText, hasSpecialRules, setupChanges, setupSummaryLines, setupSummaryText,
  type GameSetupDraft, type SetupChange,
} from '../../../lib/gameSetupDraft'
import { snapTeams, teamsSnapNotice } from '../../../lib/teamSetup'
import { SetupMapCard, SpecialRulesCard } from './SetupMapCards'
import { GameSetupCard, RulesCard } from './SetupSections'

/** Content cap; desktop splits it into the main column and the rail. */
export const SETUP_MAX_WIDTH = 1200
export const SETUP_RAIL_WIDTH = 300
const CARD_PADDING = 18

export interface GameSetupPageProps {
  mode: 'create' | 'edit'
  /** Header title ("New Game" / "Edit Game"). */
  heading: string
  /** Back button's accessible name. */
  backLabel: string
  onBack: () => void
  /** A line under the header (edit: the game and its seats). */
  meta?: string
  draft: GameSetupDraft
  onChange: (next: GameSetupDraft) => void
  /** Edit: the saved values, for the changes list and the amber dots. */
  base?: GameSetupDraft
  /** Full definition of `draft.mapId`, once loaded. */
  map: MapDef | null | undefined
  maps: MapPickerOption[]
  titlePlaceholder: string
  titleMaxLength: number
  /** Edit: the lobby's seat floor and seated count. */
  lobby?: { floor: number; seated: number }
  /** A server refusal of the last Create / Save, shown above the action. */
  error?: string | null
  mapPicker: {
    open: boolean
    onOpenChange: (open: boolean) => void
    onInspectMap: (mapId: string) => void
    onDone?: () => void
    doneLabel?: string
  }
  /** Scroll the Rules card into view once laid out (lobby "Edit rules"). */
  focusRules?: boolean
  onValidityChange?: (valid: boolean) => void
  primary: { label: string; onPress: () => void; loading?: boolean; disabled?: boolean }
  /** Edit: throw away the pending changes. */
  onDiscard?: () => void
}

export function GameSetupPage(props: GameSetupPageProps) {
  const { mode, draft, onChange, base, map, maps, lobby, mapPicker, primary, onDiscard, onValidityChange } = props
  const layout = usePageLayout({ maxWidth: SETUP_MAX_WIDTH })
  const phone = layout.tier === 'phone'
  const desktop = layout.tier === 'desktop'
  // The rail takes 320px, so desktop trades the wide page gutter for column room.
  const inset = desktop ? Math.max(24, (layout.contentWidth + layout.inset * 2 - SETUP_MAX_WIDTH) / 2) : layout.inset
  const edit = mode === 'edit'

  // The map def may lag a map change; until it arrives, use none.
  const currentMap = map && map.id === draft.mapId ? map : undefined
  const mapName = (id: string) => maps.find((m) => m.id === id)?.name ?? (currentMap?.id === id ? currentMap.name : id)

  // Seat bounds: the map's range, raised in a lobby to the highest taken seat.
  const range = mapSeatRange(currentMap)
  const seatMin = Math.max(range.min, lobby?.floor ?? 0)
  const seats = { min: Math.min(seatMin, range.max), max: range.max,
    hint: lobby ? `${lobby.seated} seated · min ${seatMin}` : range.min === range.max ? `${range.max}` : `${range.min}–${range.max}` }
  // Picking a board with a fixed opening defaults the seats to the count it
  // was built for (Hexcore: 6). Only on a map change: an edit that opens on
  // such a map keeps its saved count; a create (even one opened with ?mapId)
  // takes the default.
  // Team games skip both: their count snaps to a valid team format below.
  const teams = teamsConfigOf(draft.config)
  const defaultedMap = React.useRef<string | null>(mode === 'create' ? null : draft.mapId)
  React.useEffect(() => {
    if (!currentMap || defaultedMap.current === currentMap.id) return
    defaultedMap.current = currentMap.id
    const intended = currentMap.fixedSetup?.playerCount
    if (teams || !intended || seats.min > seats.max) return
    const target = Math.min(seats.max, Math.max(seats.min, intended))
    if (target !== draft.maxPlayers) onChange({ ...draft, maxPlayers: target })
  }, [currentMap, seats.min, seats.max, draft, onChange, teams])
  // Snap into the range when the map changes, so Create/Save can't send a
  // count the server will refuse.
  React.useEffect(() => {
    if (teams || !currentMap || seats.min > seats.max) return
    const clamped = Math.min(seats.max, Math.max(seats.min, draft.maxPlayers))
    if (clamped !== draft.maxPlayers) onChange({ ...draft, maxPlayers: clamped })
  }, [currentMap, seats.min, seats.max, draft, onChange, teams])
  // Teams: keep the size and count a valid format for the map, with a
  // one-line notice when a map change moved them (teams-config.md "Snapping").
  // A map with no team format at all goes back to Individual.
  const teamFloor = lobby?.floor ?? 0
  const [teamNotice, setTeamNotice] = React.useState<string | null>(null)
  // The notice belongs to the map change that caused it (cleared before the
  // snap below runs for a new map).
  const noticeMap = React.useRef(draft.mapId)
  React.useEffect(() => {
    if (noticeMap.current === draft.mapId) return
    noticeMap.current = draft.mapId
    setTeamNotice(null)
  }, [draft.mapId])
  React.useEffect(() => {
    if (!teams || !currentMap) return
    const snapped = snapTeams(currentMap, teams, draft.maxPlayers, teamFloor)
    if (!snapped) {
      const { teams: _teams, ...config } = draft.config
      setTeamNotice(null)
      onChange({ ...draft, config })
      return
    }
    if (snapped.players === draft.maxPlayers && snapped.teams.size === teams.size) return
    setTeamNotice(teamsSnapNotice(currentMap.name, currentMap, { size: teams.size, players: draft.maxPlayers }, { size: snapped.teams.size, players: snapped.players }))
    onChange({ ...draft, maxPlayers: snapped.players, config: { ...draft.config, teams: snapped.teams } })
  }, [currentMap, teams, teamFloor, draft, onChange])

  // Per-field validity from the steppers, aggregated for the primary action.
  const [invalid, setInvalid] = React.useState<Record<string, boolean>>({})
  const onInvalid = React.useCallback((field: string, bad: boolean) => {
    setInvalid((prev) => ((prev[field] ?? false) === bad ? prev : { ...prev, [field]: bad }))
  }, [])
  const valid = !Object.values(invalid).some(Boolean)
  React.useEffect(() => { onValidityChange?.(valid) }, [valid, onValidityChange])

  const changes = React.useMemo(() => (base ? setupChanges(base, draft, mapName) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mapName only reads maps
    [base, draft, maps])
  const changed = React.useMemo(() => new Set(changes.map((c) => c.key)), [changes])
  const summary = setupSummaryLines(draft, currentMap, mapName(draft.mapId))

  const scrollRef = React.useRef<ScrollView | null>(null)
  const focusedRules = React.useRef(false)
  const onRulesLayout = (y: number) => {
    if (!props.focusRules || focusedRules.current) return
    focusedRules.current = true
    scrollRef.current?.scrollTo({ y, animated: false })
  }

  const section = { draft, onChange, changed, phone, map: currentMap, padding: CARD_PADDING, onInvalid }
  const openPicker = () => { Keyboard.dismiss(); mapPicker.onOpenChange(true) }
  const body = <>
    <SetupMapCard mapId={draft.mapId} mapName={mapName(draft.mapId)} map={currentMap} wide={!phone} edit={edit}
      thumbWidth={Math.max(0, layout.contentWidth - 4)} onChangeMap={openPicker} />
    {currentMap && hasSpecialRules(currentMap) && <SpecialRulesCard map={currentMap} padding={phone ? CARD_PADDING : 16} />}
    <GameSetupCard {...section} titlePlaceholder={props.titlePlaceholder} titleMaxLength={props.titleMaxLength}
      seats={seats} mapName={mapName(draft.mapId)}
      teams={{ floor: teamFloor, seated: lobby?.seated ?? 0, notice: teams ? teamNotice : null }} />
    <View onLayout={(e) => onRulesLayout(e.nativeEvent.layout.y)}>
      <RulesCard {...section} />
    </View>
  </>

  return (
    <SafeAreaView className="flex-1 bg-canvas">
      <View className="flex-row items-center px-4 border-b border-olive gap-3" style={COMPACT_HEADER_STYLE}>
        <TouchableOpacity onPress={props.onBack} accessibilityLabel={props.backLabel} accessibilityRole="button" className="mr-1 p-1">
          <Text className="font-typed text-blood text-base">← Back</Text>
        </TouchableOpacity>
        <Text className="font-stencil text-ink text-lg uppercase" numberOfLines={1}>{props.heading}</Text>
        {!phone && props.meta ? <Text className="font-typed text-ink-soft text-xs flex-1" numberOfLines={1}>{props.meta}</Text> : <View className="flex-1" />}
      </View>

      {desktop ? (
        <View testID="setup-desktop" className="flex-1 flex-row" style={{ paddingHorizontal: inset, gap: 20 }}>
          <KeyboardAwareScrollView scrollRef={scrollRef} style={{ flex: 1 }} contentContainerStyle={{ paddingVertical: 18, gap: 14 }}>
            {body}
          </KeyboardAwareScrollView>
          <View style={{ width: SETUP_RAIL_WIDTH, paddingTop: 18 }}>
            <SetupRail edit={edit} summary={summary} changes={changes} primary={primary} onDiscard={onDiscard} valid={valid} error={props.error ?? null} />
          </View>
        </View>
      ) : (
        <>
          <KeyboardAwareScrollView scrollRef={scrollRef} style={{ flex: 1 }}
            contentContainerStyle={{ paddingHorizontal: layout.inset, paddingVertical: 16, gap: 16 }}>
            {phone && props.meta ? <Text className="font-typed text-ink-soft text-xs" style={{ marginBottom: -6 }}>{props.meta}</Text> : null}
            {body}
          </KeyboardAwareScrollView>
          <SetupFooter edit={edit} text={edit ? changesFooterText(changes) : setupSummaryText(summary)} hasChanges={changes.length > 0}
            primary={primary} onDiscard={onDiscard} valid={valid} inset={layout.inset} error={props.error ?? null} />
        </>
      )}

      <MapPickerModal mapId={draft.mapId} availableMaps={maps}
        onChangeMap={(mapId) => onChange({ ...draft, mapId })}
        open={mapPicker.open} onOpenChange={mapPicker.onOpenChange} onInspectMap={mapPicker.onInspectMap}
        onDone={mapPicker.onDone} doneLabel={mapPicker.doneLabel} />
    </SafeAreaView>
  )
}

type Primary = GameSetupPageProps['primary']

/** Desktop rail: "Your game" + Create, or the pending changes + Save / Discard. */
/** A server refusal, inline above the action (e.g. "This map seats 2–6 players"). */
function SetupError({ message }: { message: string | null }) {
  if (!message) return null
  return <Text testID="setup-error" accessibilityRole="alert" className="font-typed text-blood text-xs leading-4">{message}</Text>
}

function SetupRail({ edit, summary, changes, primary, onDiscard, valid, error }: {
  edit: boolean
  summary: Array<{ label: string; value: string }>
  changes: SetupChange[]
  primary: Primary
  onDiscard?: () => void
  valid: boolean
  error: string | null
}) {
  const rows = edit ? changes.map((c) => ({ label: c.label, value: `${c.from} → ${c.to}` })) : summary
  return <View testID="setup-rail" className="bg-paper border-2 border-olive rounded-xl" style={{ padding: 16, gap: 8 }}>
    <Text className="font-stencil text-[10px] uppercase tracking-widest text-olive-up">{edit ? 'Changes' : 'Your game'}</Text>
    {rows.map((r) => <View key={r.label} className="flex-row justify-between" style={{ gap: 10 }}>
      <Text className="font-typed text-ink-soft text-sm">{r.label}</Text>
      <Text className="font-typed text-ink text-sm font-bold" style={{ flexShrink: 1, textAlign: 'right' }}>{r.value}</Text>
    </View>)}
    {edit && changes.length === 0 && <Text className="font-typed text-ink-soft text-sm">No changes yet</Text>}
    <SetupError message={error} />
    <Button title={primary.label} onPress={primary.onPress} loading={primary.loading} disabled={primary.disabled || !valid} />
    {edit && onDiscard && <Button title="Discard" variant="secondary" size="sm" onPress={onDiscard} disabled={changes.length === 0}
      accessibilityLabel="Discard changes" />}
    {!edit && <Text className="font-typed text-ink-soft text-xs text-center">You can change all of this until the game starts.</Text>}
  </View>
}

/** Phone/tablet footer: one summary line and the full-width action. */
function SetupFooter({ edit, text, hasChanges, primary, onDiscard, valid, inset, error }: {
  error: string | null
  edit: boolean
  text: string
  hasChanges: boolean
  primary: Primary
  onDiscard?: () => void
  valid: boolean
  inset: number
}) {
  const action = <Button title={primary.label} onPress={primary.onPress} loading={primary.loading} disabled={primary.disabled || !valid} />
  return <View testID="setup-footer" className="border-t border-olive bg-paper" style={{ paddingHorizontal: inset, paddingTop: 12, paddingBottom: 14, gap: 8 }}>
    <Text className="font-typed text-ink-soft text-xs leading-4" numberOfLines={2}>{text}</Text>
    <SetupError message={error} />
    {edit ? <View className="flex-row" style={{ gap: 10 }}>
      <View style={{ flex: 1 }}>
        <Button title="Discard" variant="secondary" onPress={onDiscard} disabled={!hasChanges} accessibilityLabel="Discard changes" />
      </View>
      <View style={{ flex: 2 }}>{action}</View>
    </View> : action}
  </View>
}
